// Calendar agenda (CR #206) — everything in the system that carries a date,
// flattened into one typed list by GET /api/calendar/agenda. Consumed by the
// /calendar JobTimeline, the dashboard/desktop MiniCalendar and the
// ControlCenter clock panel.
import apiClient from './client';

export type AgendaType =
  | 'schedule'
  | 'task'
  | 'invoice_due'
  | 'payable'
  | 'talk'
  | 'po'
  | 'job_start'
  | 'job_end'
  | 'quote_expiry'
  | 'agreement';

export interface AgendaItem {
  date: string;             // YYYY-MM-DD
  end_date?: string | null; // YYYY-MM-DD when the item spans days
  type: AgendaType;
  label: string;
  sublabel?: string | null;
  link: string;
}

/** One fetch per visible range — the server aggregates all sources in parallel. */
export const getAgenda = async (start: string, end: string): Promise<AgendaItem[]> => {
  const response = await apiClient.get('/calendar/agenda', { params: { start, end } });
  return response.data.data;
};

/** Shared display metadata so every calendar surface renders types the same way. */
export const AGENDA_META: Record<AgendaType, { icon: string; name: string; chip: string; dot: string }> = {
  schedule:     { icon: 'event',            name: 'Scheduled work', chip: 'bg-purple-100 text-purple-700 ring-purple-200', dot: 'bg-primary' },
  task:         { icon: 'check_circle',     name: 'Job task',       chip: 'bg-blue-100 text-blue-700 ring-blue-200',       dot: 'bg-blue-600' },
  invoice_due:  { icon: 'attach_money',     name: 'Invoice due',    chip: 'bg-amber-100 text-amber-800 ring-amber-300',    dot: 'bg-critical' },
  payable:      { icon: 'receipt_long',     name: 'Bill to pay',    chip: 'bg-amber-100 text-amber-800 ring-amber-300',    dot: 'bg-critical' },
  talk:         { icon: 'campaign',         name: 'Toolbox talk',   chip: 'bg-red-100 text-red-700 ring-red-200',          dot: 'bg-red-500' },
  po:           { icon: 'local_shipping',   name: 'PO delivery',    chip: 'bg-violet-100 text-violet-700 ring-violet-200', dot: 'bg-violet-600' },
  job_start:    { icon: 'flag',             name: 'Job starts',     chip: 'bg-green-100 text-green-700 ring-green-200',    dot: 'bg-success' },
  job_end:      { icon: 'sports_score',     name: 'Job ends',       chip: 'bg-orange-100 text-orange-700 ring-orange-200', dot: 'bg-warning' },
  quote_expiry: { icon: 'hourglass_bottom', name: 'Quote expires',  chip: 'bg-teal-100 text-teal-700 ring-teal-200',       dot: 'bg-teal-600' },
  agreement:    { icon: 'history_edu',      name: 'Agreement',      chip: 'bg-indigo-100 text-indigo-700 ring-indigo-200', dot: 'bg-indigo-600' },
};

/** Types the /calendar month cells add as chips — schedule events and job
 *  start/end markers are already drawn by the calendar itself. */
export const AGENDA_EXTRA_TYPES: AgendaType[] = [
  'invoice_due', 'payable', 'task', 'talk', 'po', 'quote_expiry', 'agreement',
];

/** Expand items into a per-day map (multi-day items land on every covered day). */
export const agendaByDay = (items: AgendaItem[]): Map<string, AgendaItem[]> => {
  const map = new Map<string, AgendaItem[]>();
  for (const it of items) {
    const endKey = it.end_date && it.end_date > it.date ? it.end_date : it.date;
    let cur = new Date(it.date + 'T12:00:00');
    const end = new Date(endKey + 'T12:00:00');
    let guard = 0;
    while (cur <= end && guard < 62) {
      const k = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(it);
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
      guard++;
    }
  }
  return map;
};
